import React, { useState, useEffect, useCallback, useRef } from "react";
import Login from "./components/Login/Login";
import MainMenu from "./components/MainMenu/MainMenu";
import TournamentSeats from "./components/Tournament/TournamentSeats";
import PlayerSetup from "./components/Game/PlayerSetup";
import RoleAssignment from "./components/Game/RoleAssignment";
import DayPhase from "./components/Game/DayPhase";
import Voting from "./components/Game/Voting";
import NightPhase from "./components/Game/NightPhase";

import GameResults from "./components/Game/GameResults";
import ClubEvents from "./components/Club/ClubEvents";
import { useGameState } from "./hooks/useGameState";
import { useStreamSync } from "./hooks/useStreamSync";
import { apiService } from "./services/api";
import { Tournament, Club, Player, GameRole } from "./types";
import { dictionaries } from "./utils/dictionary";
import { getCurrentLocalDateTime } from "./utils/dateUtils";
import { applyRTL } from "./utils/rtl";
import { getCookie } from "./utils/cookies";
import "./App.css";

interface UserData {
  id: number;
  nickname: string;
  avatar: string;
  token: string;
}

enum AppState {
  LOGIN,
  MAIN_MENU,
  TOURNAMENT_SEATS,
  CLUB_EVENTS,
  PLAYER_SETUP,
  GAME_ROLES,
  GAME_DAY,
  GAME_VOTING,
  LAST_WORDS,
  GAME_NIGHT,
  MORNING_TRANSITION,
  GAME_RESULTS,
}

function App() {
  const [appState, setAppState] = useState<AppState>(AppState.LOGIN);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [language, setLanguage] = useState(0);
  const [selectedTournament, setSelectedTournament] =
    useState<Tournament | null>(null);
  const [selectedClub, setSelectedClub] = useState<Club | null>(null);
  const [currentEvent, setCurrentEvent] = useState<any>(null);
  const [eventParticipants, setEventParticipants] = useState<Player[]>([]);
  const [protocolPlayerIds, setProtocolPlayerIds] = useState<number[]>([]); // Original protocol player IDs for tournament games
  const [gamePlayers, setGamePlayers] = useState<Player[]>([]);
  const [gameJudge, setGameJudge] = useState<Player | undefined>(undefined);
  const [gameRoles, setGameRoles] = useState<GameRole[]>([]);
  const [nominations, setNominations] = useState<Record<string, string>>({});
  const [gameTableNumber, setGameTableNumber] = useState<number>(1);
  const [gameNumber, setGameNumber] = useState<number>(1);
  const [handledFouls, setHandledFouls] = useState<Set<string>>(new Set());
  const [eliminatedPlayers, setEliminatedPlayers] = useState<Player[]>([]);
  const [eliminatedPlayerNumbers, setEliminatedPlayerNumbers] = useState<number[]>([]);
  const [eliminationType, setEliminationType] = useState<"voted" | "killed">(
    "voted"
  );
  
  // Stream settings state
  const [streamBanner, setStreamBanner] = useState<Record<string, any>>(() => {
    const theme = localStorage.getItem('streamTheme') || 'dark';
    const language = localStorage.getItem('streamLanguage');
    return {
      theme,
      language: language !== null ? parseInt(language) : 1,
    };
  });

  // Track if voting is in progress (to disable voting button)
  const [votingInProgress, setVotingInProgress] = useState(false);

  // Technical break state (overrides gamePhase for streamsync only, doesn't change gameState)
  const [technicalBreakGamePhase, setTechnicalBreakGamePhase] = useState<number | null>(null);

  // Draw tracking (for 3 consecutive mafia misses)
  const [drawCount, setDrawCount] = useState(0);
  const [drawPlayers, setDrawPlayers] = useState(0);

  // State backup for "Back to Day" functionality
  const [dayStateBackup, setDayStateBackup] = useState<{
    gameState: any;
    nominations: Record<string, string>;
    handledFouls: Set<string>;
    firstDayEliminations: number;
  } | null>(null);

  // State backup for "Undo Night" functionality
  const [nightStateBackup, setNightStateBackup] = useState<{
    gameState: any;
    nominations: Record<string, string>;
    handledFouls: Set<string>;
    firstDayEliminations: number;
  } | null>(null);

  // Force re-render key for components after state restoration
  const [renderKey, setRenderKey] = useState(0);

  // Track players eliminated on first day for best move logic
  const [firstDayEliminations, setFirstDayEliminations] = useState(0);

  // Track if streaming should be enabled
  const [streamEnabled, setStreamEnabled] = useState(false);

  // Track stream sync errors
  const [streamError, setStreamError] = useState(false);

  // Track if we're currently restoring game state
  const [isRestoring, setIsRestoring] = useState(false);

  // Track if gamePhase: -2 was already set (to avoid infinite loop)
  const playersReadySetRef = useRef(false);
  // Track last player IDs to detect changes
  const lastPlayerIdsRef = useRef<number[]>([]);

  const {
    gameState,
    loadGameState,
    clearSavedState,
    resetGameState,
    updateGameState,
    replaceGameState,
  } = useGameState();

  // Centralized function to reset all game state
  const resetAllGameState = useCallback(() => {
    resetGameState();
    clearSavedState();
    localStorage.removeItem("gameData");
    setGamePlayers([]);
    setGameJudge(undefined);
    setGameRoles([]);
    setNominations({});
    setFirstDayEliminations(0);
    setVotingInProgress(false);
    setHandledFouls(new Set());
    setEliminatedPlayers([]);
    setEliminatedPlayerNumbers([]);
    setEliminationType("voted");
    setTechnicalBreakGamePhase(null);
    setStreamError(false);
    setDayStateBackup(null);
    setNightStateBackup(null);
    setDrawCount(0);
    setDrawPlayers(0);
    playersReadySetRef.current = false;
    lastPlayerIdsRef.current = [];
  }, [resetGameState, clearSavedState]);

  const saveGameData = useCallback(() => {
    const gameData = {
      gamePlayers,
      gameJudge,
      gameRoles,
      nominations,
      gameTableNumber,
      gameNumber,
      handledFouls: Array.from(handledFouls),
      eliminatedPlayers,
      eliminationType,
      firstDayEliminations,
      selectedClub,
      selectedTournament,
      currentEvent,
      timestamp: Date.now(),
    };
    localStorage.setItem("gameData", JSON.stringify(gameData));
  }, [
    gamePlayers,
    gameJudge,
    gameRoles,
    nominations,
    gameTableNumber,
    gameNumber,
    handledFouls,
    eliminatedPlayers,
    eliminationType,
    firstDayEliminations,
    selectedClub,
    selectedTournament,
    currentEvent,
  ]);

  const restoreGameFromSavedState = useCallback(() => {
    // Restore additional game data from localStorage
    const savedGameData = localStorage.getItem("gameData");
    if (savedGameData) {
      try {
        const data = JSON.parse(savedGameData);
        console.log("Restoring game data:", data);
        if (data.gamePlayers) {
          console.log("Restoring gamePlayers:", data.gamePlayers);
          setGamePlayers(data.gamePlayers);
        }
        if (data.gameJudge) setGameJudge(data.gameJudge);
        if (data.gameRoles) setGameRoles(data.gameRoles);
        if (data.nominations) setNominations(data.nominations);
        if (data.gameTableNumber) setGameTableNumber(data.gameTableNumber);
        if (data.gameNumber) setGameNumber(data.gameNumber);
        if (data.handledFouls) setHandledFouls(new Set(data.handledFouls));
        if (data.eliminatedPlayers)
          setEliminatedPlayers(data.eliminatedPlayers);
        if (data.eliminationType) setEliminationType(data.eliminationType);
        if (data.firstDayEliminations !== undefined)
          setFirstDayEliminations(data.firstDayEliminations);
        if (data.selectedClub) setSelectedClub(data.selectedClub);
        if (data.selectedTournament)
          setSelectedTournament(data.selectedTournament);
        if (data.currentEvent) setCurrentEvent(data.currentEvent);
      } catch (error) {
        console.error("Failed to restore game data:", error);
      }
    } else {
      console.log("No saved game data found");
    }
    // App state will be set by the separate useEffect that watches gameState changes
  }, []);

  const getAppStateFromGamePhase = (gamePhase: number): AppState => {
    // gamePhase mapping:
    // -3: Player setup (not all players selected)
    // -2: Player setup (all players ready)
    // -1: Role assignment
    // 0: First day (day phase)
    // 1: First night (night phase)
    // 2: Second day (day phase)
    // 3: Second night (night phase)
    // ... even numbers = day, odd numbers = night
    // 99+: Game results
    
    switch (gamePhase) {
      case -3:
      case -2:
        return AppState.PLAYER_SETUP;
      case -1:
        return AppState.GAME_ROLES;
      default:
        if (gamePhase >= 99) {
          return AppState.GAME_RESULTS;
        } else if (gamePhase % 2 === 0) {
          // Even gamePhase = day phase (0, 2, 4, 6...)
          return AppState.GAME_DAY;
        } else {
          // Odd gamePhase = night phase (1, 3, 5, 7...)
          return AppState.GAME_NIGHT;
        }
    }
  };

  // Auto-save game data when key state changes
  useEffect(() => {
    if (gameState.gameStage > 0) {
      saveGameData();
    }
  }, [gameState.gameStage, saveGameData]);

  // Also save when gamePlayers changes (important for role assignment phase)
  useEffect(() => {
    if (gameState.gameStage > 0 && gamePlayers.length > 0) {
      console.log("Saving game data after gamePlayers update:", gamePlayers);
      saveGameData();
    }
  }, [gamePlayers, gameState.gameStage, saveGameData]);

  // Save when gameRoles changes
  useEffect(() => {
    if (gameState.gameStage > 0 && gameRoles.length > 0) {
      console.log("Saving game data after gameRoles update:", gameRoles);
      saveGameData();
    }
  }, [gameRoles, gameState.gameStage, saveGameData]);

  // Effect to handle app state restoration after gameState is loaded
  useEffect(() => {
    // Check if we're in the process of restoring and gameState has been loaded
    if (isRestoring && gameState.gameStage > 0) {
      const correctAppState = getAppStateFromGamePhase(gameState.gamePhase);
      setAppState(correctAppState);
      // Clear restoring flag after state is restored
      setIsRestoring(false);
    }
  }, [gameState.gameStage, gameState.gamePhase, isRestoring]);

  // Effect to control stream sync enabled state
  useEffect(() => {
    // Enable streaming when:
    // - Game has started (gameStage >= 1, includes PlayerSetup)
    // - Not in MAIN_MENU or LOGIN states
    // - Not currently restoring game state
    const isGameActive =
      gameState.gameStage >= 1 &&
      appState !== AppState.MAIN_MENU &&
      appState !== AppState.LOGIN &&
      !isRestoring;

    // Log streaming state changes (development only)
    if (process.env.NODE_ENV === "development") {
      console.log("[StreamSync] Streaming enabled state changed:", {
        enabled: isGameActive,
        gameStage: gameState.gameStage,
        appState,
        isRestoring,
      });
    }

    setStreamEnabled(isGameActive);
  }, [gameState.gameStage, appState, isRestoring]);

  useEffect(() => {
    // Check if user is already logged in (check both localStorage and cookies for legacy compatibility)
    let token = localStorage.getItem("token") || getCookie("token");
    let myName = localStorage.getItem("myName") || getCookie("myName");
    let myAvatar = localStorage.getItem("myAvatar") || getCookie("myAvatar");
    let myID = localStorage.getItem("myID") || getCookie("myID");
    let savedLanguage = localStorage.getItem("l") || getCookie("l");

    // Sync data from cookies to localStorage if needed
    if (token && !localStorage.getItem("token")) {
      localStorage.setItem("token", token);
      console.log("[Auth] Token synced from cookies to localStorage");
    }

    if (myName && !localStorage.getItem("myName")) {
      localStorage.setItem("myName", myName);
    }

    if (myAvatar && !localStorage.getItem("myAvatar")) {
      localStorage.setItem("myAvatar", myAvatar);
    }

    if (myID && !localStorage.getItem("myID")) {
      localStorage.setItem("myID", myID);
    }

    if (savedLanguage && !localStorage.getItem("l")) {
      localStorage.setItem("l", savedLanguage);
    }

    // Auto-login if we have all required data
    if (token && myName && myAvatar && myID) {
      setUserData({
        id: parseInt(myID),
        nickname: myName,
        avatar: myAvatar,
        token,
      });
      const lang = savedLanguage ? parseInt(savedLanguage) : 0;
      setLanguage(lang);
      applyRTL(lang); // Apply RTL if needed
      setAppState(AppState.MAIN_MENU);
      console.log("[Auth] User auto-logged in from stored credentials (localStorage + cookies)");
    } else if (token) {
      // Token exists but user data is incomplete
      console.log("[Auth] Token found but user data incomplete - user needs to login");
    }
  }, []); // Remove dependencies to prevent multiple calls

  // Apply RTL when language changes
  useEffect(() => {
    applyRTL(language);
  }, [language]);

  // Separate effect for game state restoration
  useEffect(() => {
    if (userData && appState === AppState.MAIN_MENU) {
      // Check for saved game state only once when user is logged in
      const saved = localStorage.getItem("reGameState");
      if (saved) {
        try {
          const state = JSON.parse(saved);

          // Check if state is not too old (15 minutes) and game was actually started
          if (
            Date.now() - state.timestamp <= 15 * 60 * 1000 &&
            state.gameState.gameStage > 0
          ) {
            // Show restore game dialog
            const shouldRestore = window.confirm(
              dictionaries[language].ph.restoreGameMsg
            );

            if (shouldRestore) {
              // Set restoring flag to disable streaming during restoration
              setIsRestoring(true);

              // Load game state first
              loadGameState();

              // Restore additional data
              restoreGameFromSavedState();

              // Note: appState will be set in the useEffect that monitors isRestoring
            } else {
              // User declined restoration - clear all saved data
              clearSavedState();
              localStorage.removeItem("gameData");
            }
          } else {
            // State is too old or game wasn't started - clear stale data
            clearSavedState();
            localStorage.removeItem("gameData");
          }
        } catch (error) {
          console.error("Failed to check saved game state:", error);
          // Invalid/corrupted data - clear it
          clearSavedState();
          localStorage.removeItem("gameData");
        }
      }
    }
  }, [
    userData,
    appState,
    loadGameState,
    clearSavedState,
    language,
    restoreGameFromSavedState,
  ]); // Add missing dependencies

  // Debug: Track changes to playersLeaved
  useEffect(() => {
    console.log(
      "App.tsx: gameState.playersLeaved changed to:",
      gameState.playersLeaved
    );
  }, [gameState.playersLeaved]);

  // Initialize stream sync to push game state updates to backend
  useStreamSync({
    gameState,
    gamePlayers,
    gameJudge,
    gameRoles,
    nominations,
    tournamentId: selectedTournament?.tournament_id,
    clubId: selectedClub?.id,
    tableNumber: gameTableNumber,
    gameNumber: gameNumber,
    streamBanner,
    enabled: streamEnabled,
    overrideGamePhase: technicalBreakGamePhase,
    onStreamError: setStreamError,
  });

  const handleLogin = (loginData: UserData) => {
    setUserData(loginData);
    // Read language from localStorage that was just set during login
    const savedLanguage = localStorage.getItem("l");
    if (savedLanguage) {
      setLanguage(parseInt(savedLanguage));
    }
    setAppState(AppState.MAIN_MENU);
  };

  const handleLanguageChange = (newLanguage: number) => {
    localStorage.setItem('l', newLanguage.toString());
    setLanguage(newLanguage);
    applyRTL(newLanguage);
  };

  const handleStreamSettingsChange = useCallback((theme: string, streamLang: number) => {
    setStreamBanner({ theme, language: streamLang });
  }, []);

  // Generate stream link once based on game type
  const streamLink = React.useMemo(() => {
    if (!gameTableNumber) return undefined;
    if (selectedClub?.id) {
      return `https://app.mafiaworldtour.org/clubstream/${selectedClub.id}/${gameTableNumber}`;
    }
    if (selectedTournament?.tournament_id) {
      return `https://app.mafiaworldtour.org/hud/${selectedTournament.tournament_id}/${gameTableNumber}`;
    }
    return undefined;
  }, [selectedClub?.id, selectedTournament?.tournament_id, gameTableNumber]);

  const handleLogout = () => {
    apiService.logout();
    setUserData(null);
    setLanguage(0); // Reset language to default
    setSelectedTournament(null);
    setSelectedClub(null);
    setCurrentEvent(null);
    resetAllGameState();
    setAppState(AppState.LOGIN);
  };

  const handleTournamentSelect = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    setSelectedClub(null);
    setCurrentEvent(null);
    setAppState(AppState.TOURNAMENT_SEATS);
  };

  const handleClubSelect = (club: Club) => {
    setSelectedTournament(null);
    setSelectedClub(club);
    // Note: we keep currentEvent so we can restore the opened event
    setAppState(AppState.CLUB_EVENTS);
  };

  const handleTournamentGameSelect = async (
    partNumber: number,
    tableNumber: number,
    sessionNumber: number,
    isFinal: boolean
  ) => {
    setSelectedClub(null);
    setCurrentEvent(null);
    // Load protocol for selected tournament game
    try {
      const response = await apiService.loadProtocol({
        tournament_id: selectedTournament!.tournament_id,
        part_number: partNumber,
        table_number: tableNumber,
        session_number: sessionNumber,
      });

      if (response.success) {
        const protocol = response.success;

        // Initialize game setup
        resetAllGameState();

        // Set tournament game metadata
        setGameTableNumber(tableNumber);
        setGameNumber(sessionNumber);

        // Store tournament context
        updateGameState({
          gameStage: 1,
          currPart: partNumber,
          currTable: tableNumber,
          gnumber: sessionNumber,
          isFinal: isFinal ? 1 : 0,
        });

        // Convert protocol players to Player format
        // These are the actual players sitting at the table with their seat numbers (1-10)
        if (protocol.protocol && protocol.protocol.length > 0) {
          const protocolPlayers: Player[] = protocol.protocol.map((p: any) => ({
            user_id: p.user_id,
            nickname: p.nickname,
            avatar: p.avatar || "https://app.mafiaworldtour.org/img/alive.png",
            club_name: p.club_name,
            club_logo: p.club_logo,
            city: p.city,
            masters: p.masters,
            games_played_red: p.games_played_red || 0,
            games_played_black: p.games_played_black || 0,
            games_played_sheriff: p.games_played_sheriff || 0,
            games_played_don: p.games_played_don || 0,
            wins_red: p.wins_red || 0,
            wins_black: p.wins_black || 0,
            wins_sheriff: p.wins_sheriff || 0,
            wins_don: p.wins_don || 0,
            c_games_played_red: p.c_games_played_red || 0,
            c_games_played_black: p.c_games_played_black || 0,
            c_games_played_sheriff: p.c_games_played_sheriff || 0,
            c_games_played_don: p.c_games_played_don || 0,
            c_wins_red: p.c_wins_red || 0,
            c_wins_black: p.c_wins_black || 0,
            c_wins_sheriff: p.c_wins_sheriff || 0,
            c_wins_don: p.c_wins_don || 0,
          }));

          // Set eventParticipants - PlayerSetup will auto-fill from this
          // These are the seated players in order (positions 1-10)
          setEventParticipants(protocolPlayers);
          
          // Store original protocol player IDs for is_replacement calculation
          setProtocolPlayerIds(protocolPlayers.map(p => p.user_id));
        }

        // Set judge if available with level
        if (protocol.referee && protocol.referee.user_id) {
          const judgeData: Player = {
            user_id: protocol.referee.user_id,
            nickname: protocol.referee.name || protocol.referee.nickname,
            avatar:
              protocol.referee.avatar ||
              "https://app.mafiaworldtour.org/img/alive.png",
            level: protocol.referee.level || 0, // Include judge level
            games_played_red: 0,
            games_played_black: 0,
            games_played_sheriff: 0,
            games_played_don: 0,
            wins_red: 0,
            wins_black: 0,
            wins_sheriff: 0,
            wins_don: 0,
            c_games_played_red: 0,
            c_games_played_black: 0,
            c_games_played_sheriff: 0,
            c_games_played_don: 0,
            c_wins_red: 0,
            c_wins_black: 0,
            c_wins_sheriff: 0,
            c_wins_don: 0,
          };
          
          setGameJudge(judgeData);
        }

        setAppState(AppState.PLAYER_SETUP);
      } else {
        alert("Failed to load tournament protocol");
      }
    } catch (error) {
      console.error("Failed to load tournament protocol:", error);
      alert("Failed to load tournament protocol");
    }
  };

  const handleStartGame = (
    clubId?: number,
    eventId?: number,
    tableId?: number,
    nextGameNumber?: number,
    participants?: any[]
  ) => {
    // Initialize game setup
    resetAllGameState();
    if (tableId) setGameTableNumber(tableId);
    if (eventId) setCurrentEvent({ id: eventId });

    // Set gameStage to 1 for PlayerSetup phase
    updateGameState({ gameStage: 1 });

    // Set game number (passed from ClubEvents or default to 1)
    setGameNumber(nextGameNumber || 1);

    // Convert participants to Player format (passed from ClubEvents)
    if (participants && participants.length > 0) {
      const participantPlayers: Player[] = participants.map((p: any) => ({
        user_id: p.user_id,
        nickname: p.nickname,
        avatar: p.avatar || "https://app.mafiaworldtour.org/img/alive.png",
        games_played_red: p.count || 0,
        games_played_black: 0,
        games_played_sheriff: 0,
        games_played_don: 0,
        wins_red: 0,
        wins_black: 0,
        wins_sheriff: 0,
        wins_don: 0,
        c_games_played_red: 0,
        c_games_played_black: 0,
        c_games_played_sheriff: 0,
        c_games_played_don: 0,
        c_wins_red: 0,
        c_wins_black: 0,
        c_wins_sheriff: 0,
        c_wins_don: 0,
      }));

      setEventParticipants(participantPlayers);
    } else {
      setEventParticipants([]);
    }

    setAppState(AppState.PLAYER_SETUP);
  };

  const handlePlayersChange = useCallback(
    (players: Player[], allReady: boolean) => {
      // Reset flag if not all players ready (e.g., player was removed)
      if (!allReady) {
        playersReadySetRef.current = false;
        lastPlayerIdsRef.current = [];
      }

      // Update gamePlayers only if changed
      setGamePlayers((prev) => {
        // Check if players actually changed
        if (
          prev.length === players.length &&
          prev.every((p, i) => p?.user_id === players[i]?.user_id)
        ) {
          return prev; // No change, return same reference
        }
        return players;
      });

      // When all 10 players are selected, update gamePhase to -2
      if (allReady && players.length === 10) {
        // Check if player list changed
        const currentPlayerIds = players.map((p) => p.user_id);
        const playersChanged =
          lastPlayerIdsRef.current.length !== currentPlayerIds.length ||
          !lastPlayerIdsRef.current.every(
            (id, i) => id === currentPlayerIds[i]
          );

        // Only update if players changed OR if this is the first time all ready
        if (playersChanged || !playersReadySetRef.current) {
          playersReadySetRef.current = true;
          lastPlayerIdsRef.current = currentPlayerIds;
          updateGameState({
            players: currentPlayerIds,
            playerNames: players.map((p) => p.nickname),
            playerAvatars: players.map((p) => p.avatar),
            gamePhase: -2, // All players selected
          });
        }
      }
    },
    [updateGameState]
  );

  const handlePlayersReady = (players: Player[], judge?: Player) => {
    // Store judge for the game (players already stored in handlePlayersChange)
    setGameJudge(judge);

    // Transition to role assignment
    updateGameState({
      gameStage: 2,
      gamePhase: -1, // Role assignment phase
    });
    setAppState(AppState.GAME_ROLES);
  };

  const handleRolesReady = (roles: GameRole[]) => {
    // Store roles and update game state
    setGameRoles(roles);
    updateGameState({
      playerRoles: roles,
      gameStage: 3,
      gamePhase: 0, // First day (gamePhase starts at 0 for first day)
      launchTime: getCurrentLocalDateTime(),
      // Initialize timer (paused by default)
      //timerAccumulated: 0,
      //timerStartedAt: undefined,
    });

    // Save will happen automatically via useEffect when gameRoles updates

    // Move to day phase
    setAppState(AppState.GAME_DAY);
  };

  const handleVotingStart = (dayNominations: Record<string, string>) => {
    // Save current state before voting starts
    saveDayState();

    setNominations(dayNominations);
    setVotingInProgress(true); // Block voting button
    // gamePhase stays the same during voting (it's part of the day phase)
    setAppState(AppState.GAME_VOTING);
  };

  const handleSetVotingInProgress = (inProgress: boolean) => {
    setVotingInProgress(inProgress);
  };

  const handleInlineVotingComplete = (results: any) => {
    // DON'T clear backup here - keep it for potential "Back to Day"
    // setDayStateBackup(null);

    // Track first day eliminations for best move logic
    if (gameState.nightCount === 0 && results.eliminatedPlayers.length > 0) {
      setFirstDayEliminations(results.eliminatedPlayers.length);
    }

    // Handle voting results directly without going to separate voting screen
    if (results.eliminatedPlayers.length > 0) {
      // Add to playersLeavingNow instead of playersLeaved
      // They will be moved to playersLeaved when farewell completes
      // Filter out players already in playersLeaved (e.g., disqualified during voting)
      const playersToAdd = results.eliminatedPlayers.filter(
        (playerNum: number) => !gameState.playersLeaved.includes(playerNum)
      );

      const newPlayersLeavingNow = [
        ...gameState.playersLeavingNow,
        ...playersToAdd,
      ];
      updateGameState({
        playersLeavingNow: newPlayersLeavingNow,
      });

      // Set up last words for eliminated players (only those not already left)
      if (playersToAdd.length > 0) {
        const eliminatedPlayerData = playersToAdd.map(
          (playerNum: number) => gamePlayers[playerNum - 1]
        );
        setEliminatedPlayers(eliminatedPlayerData);
        setEliminatedPlayerNumbers(playersToAdd);
        setEliminationType("voted");
        // Voting is still in progress until we go to night
        setAppState(AppState.LAST_WORDS);
      } else {
        // All players already disqualified, skip last words and go to night
        setVotingInProgress(false);
        setNominations({}); // Clear nominations when going to night
        const nextNightCount = gameState.nightCount + 1;
        updateGameState({
          nightCount: nextNightCount,
          gamePhase: nextNightCount * 2 - 1, // Night phase: 1, 3, 5, 7...
        });
        setAppState(AppState.GAME_NIGHT);
      }
    } else {
      // No eliminations, go directly to night
      setVotingInProgress(false); // Reset voting state
      setNominations({}); // Clear nominations when going to night
      const nextNightCount = gameState.nightCount + 1;
      updateGameState({
        nightCount: nextNightCount,
        gamePhase: nextNightCount * 2 - 1, // Night phase: 1, 3, 5, 7...
      });
      setAppState(AppState.GAME_NIGHT);
    }
  };

  const handleVotingComplete = (results: any) => {
    // Track first day eliminations for best move logic
    if (gameState.nightCount === 0 && results.eliminatedPlayers.length > 0) {
      setFirstDayEliminations(results.eliminatedPlayers.length);
    }

    // Update game state with voting results
    if (results.eliminatedPlayers.length > 0) {
      // Add to playersLeavingNow instead of playersLeaved
      // They will be moved to playersLeaved when farewell completes
      // Filter out players already in playersLeaved (e.g., disqualified during voting)
      const playersToAdd = results.eliminatedPlayers.filter(
        (playerNum: number) => !gameState.playersLeaved.includes(playerNum)
      );

      const newPlayersLeavingNow = [
        ...gameState.playersLeavingNow,
        ...playersToAdd,
      ];
      updateGameState({
        playersLeavingNow: newPlayersLeavingNow,
      });

      // Set up last words for eliminated players (only those not already left)
      if (playersToAdd.length > 0) {
        const eliminatedPlayerData = playersToAdd.map(
          (playerNum: number) => gamePlayers[playerNum - 1]
        );
        setEliminatedPlayers(eliminatedPlayerData);
        setEliminatedPlayerNumbers(playersToAdd);
        setEliminationType("voted");
        setAppState(AppState.LAST_WORDS);
      } else {
        // All players already disqualified, skip last words and go to night
        setNominations({}); // Clear nominations when going to night
        setAppState(AppState.GAME_NIGHT);
      }
    } else {
      // No eliminations, go directly to night
      setNominations({}); // Clear nominations when going to night
      setAppState(AppState.GAME_NIGHT);
    }
  };

  const handleGoToNight = () => {
    // Check game end conditions before going to night
    // Consider both playersLeaved and playersLeavingNow as eliminated
    const allLeftPlayers = [
      ...gameState.playersLeaved,
      ...(gameState.playersLeavingNow || []),
    ];

    let aliveRed = 0;
    let aliveBlack = 0;

    gameRoles.forEach((role, index) => {
      const playerNumber = index + 1;
      if (!allLeftPlayers.includes(playerNumber)) {
        if (role === "red" || role === "sheriff") {
          aliveRed++;
        } else if (role === "black" || role === "don") {
          aliveBlack++;
        }
      }
    });

    // Check win conditions
    if (aliveBlack === 0) {
      // Citizens win
      updateGameState({
        pobeda: "red",
        gamePhase: 99, // Game results phase
        endTime: getCurrentLocalDateTime(),
      });
      setDayStateBackup(null);
      setNightStateBackup(null);
      setVotingInProgress(false);
      setAppState(AppState.GAME_RESULTS);
      return;
    } else if (aliveBlack >= aliveRed) {
      // Mafia wins
      updateGameState({
        pobeda: "black",
        gamePhase: 99, // Game results phase
        endTime: getCurrentLocalDateTime(),
      });
      setDayStateBackup(null);
      setNightStateBackup(null);
      setVotingInProgress(false);
      setAppState(AppState.GAME_RESULTS);
      return;
    }

    // Game continues - go to night
    // Save current day state before going to night (for potential "Back to Day")
    saveDayState();

    setVotingInProgress(false); // Reset voting state when going to night
    setNominations({}); // Clear nominations when going to night
    const nextNightCount = gameState.nightCount + 1;
    updateGameState({
      nightCount: nextNightCount,
      gamePhase: nextNightCount * 2 - 1, // Night phase: 1, 3, 5, 7...
    });

    setAppState(AppState.GAME_NIGHT);
  };

  const handleNominationChange = (playerIndex: number, nomination: string) => {
    const newNominations = { ...nominations };

    if (nomination === "" || nomination === "0") {
      delete newNominations[playerIndex.toString()];
    } else {
      // Validate nomination
      const nomNumber = parseInt(nomination);
      if (
        nomNumber >= 1 &&
        nomNumber <= 10 &&
        !gameState.playersLeaved.includes(nomNumber)
      ) {
        newNominations[playerIndex.toString()] = nomination;
      }
    }

    setNominations(newNominations);
  };

  const saveDayState = () => {
    // Save current state before voting starts (for inline voting)
    const backup = {
      gameState: {
        ...gameState,
        // Explicitly save critical arrays to ensure they're properly copied
        playersLeaved: [...gameState.playersLeaved],
        playersLeavingNow: [...(gameState.playersLeavingNow || [])],
        playerFauls: [...gameState.playerFauls],
        penaltyListToday: [...gameState.penaltyListToday],
        penaltyListTomorrow: [...gameState.penaltyListTomorrow],
      },
      nominations: { ...nominations },
      handledFouls: new Set(handledFouls),
      firstDayEliminations: firstDayEliminations,
    };

    console.log("Saving day state backup:", {
      playersLeaved: backup.gameState.playersLeaved,
      playersLeavingNow: backup.gameState.playersLeavingNow,
      nominations: backup.nominations,
      firstDayEliminations: backup.firstDayEliminations,
      backupExists: !!backup,
    });

    setDayStateBackup(backup);
  };

  const saveNightState = () => {
    // Save current state before night actions (for potential undo)
    const backup = {
      gameState: {
        ...gameState,
        // Explicitly save critical arrays to ensure they're properly copied
        playersLeaved: [...gameState.playersLeaved],
        playersLeavingNow: [...(gameState.playersLeavingNow || [])],
        playersLeavedGP: [...gameState.playersLeavedGP],
        playerFauls: [...gameState.playerFauls],
        penaltyListToday: [...gameState.penaltyListToday],
        penaltyListTomorrow: [...gameState.penaltyListTomorrow],
        otstrel: [...gameState.otstrel],
        proverkiDon: [...gameState.proverkiDon],
        proverkiSheriff: [...gameState.proverkiSheriff],
        vanga: [...gameState.vanga],
      },
      nominations: { ...nominations },
      handledFouls: new Set(handledFouls),
      firstDayEliminations: firstDayEliminations,
    };

    console.log("Saving night state backup:", {
      playersLeaved: backup.gameState.playersLeaved,
      playersLeavingNow: backup.gameState.playersLeavingNow,
      nightCount: backup.gameState.nightCount,
      firstDayEliminations: backup.firstDayEliminations,
      backupExists: !!backup,
    });

    setNightStateBackup(backup);
  };

  const handleBackToDay = () => {
    console.log("handleBackToDay called, backup exists:", !!dayStateBackup);

    // Restore state from backup if available
    if (dayStateBackup) {
      console.log("Restoring state from backup:", {
        currentPlayersLeaved: gameState.playersLeaved,
        backupPlayersLeaved: dayStateBackup.gameState.playersLeaved,
        currentNominations: nominations,
        backupNominations: dayStateBackup.nominations,
        currentFirstDayEliminations: firstDayEliminations,
        backupFirstDayEliminations: dayStateBackup.firstDayEliminations,
      });

      // Restore game state completely (full replacement, not merge)
      // Create a new object to ensure React detects the change
      console.log("[BackToDay] Restoring gamePhase:", {
        current: gameState.gamePhase,
        backup: dayStateBackup.gameState.gamePhase,
      });
      replaceGameState({ ...dayStateBackup.gameState });

      // Restore nominations
      setNominations(dayStateBackup.nominations);

      // Restore handled fouls
      setHandledFouls(dayStateBackup.handledFouls);

      // Restore first day eliminations counter
      setFirstDayEliminations(dayStateBackup.firstDayEliminations);

      // Clear eliminated players state
      setEliminatedPlayers([]);
      setEliminatedPlayerNumbers([]);
      setEliminationType("voted");

      // Reset voting state
      setVotingInProgress(false);

      // Clear backup
      setDayStateBackup(null);

      // Force re-render of components
      setRenderKey((prev) => prev + 1);

      console.log(
        "State restoration completed, new playersLeaved should be:",
        dayStateBackup.gameState.playersLeaved
      );
    } else {
      console.log(
        "No backup available for restoration - backup was cleared or never created"
      );
      
      // If no backup (e.g., after page refresh), at least fix gamePhase
      // When returning from night to day, gamePhase should be: nightCount * 2
      const currentGamePhase = gameState.gamePhase;
      const prevDayNightCount = gameState.nightCount - 1
      const correctDayGamePhase = prevDayNightCount * 2;
      
      if (currentGamePhase !== correctDayGamePhase) {
        console.log("[BackToDay] Fixing gamePhase:", {
          current: currentGamePhase,
          correct: correctDayGamePhase,
          nightCount: gameState.nightCount,
        });
        updateGameState({
          gamePhase: correctDayGamePhase,
          nightCount: prevDayNightCount
        });
      }
    }

    // Return from night phase to day phase
    setAppState(AppState.GAME_DAY);
  };

  const handleUndoNight = () => {
    console.log("handleUndoNight called, backup exists:", !!nightStateBackup);

    // Restore state from backup if available
    if (nightStateBackup) {
      console.log("Restoring night state from backup:", {
        currentPlayersLeaved: gameState.playersLeaved,
        backupPlayersLeaved: nightStateBackup.gameState.playersLeaved,
        currentNightCount: gameState.nightCount,
        backupNightCount: nightStateBackup.gameState.nightCount,
        currentFirstDayEliminations: firstDayEliminations,
        backupFirstDayEliminations: nightStateBackup.firstDayEliminations,
      });

      // Restore game state completely (full replacement, not merge)
      // Create a new object to ensure React detects the change
      replaceGameState({ ...nightStateBackup.gameState });

      // Restore nominations
      setNominations(nightStateBackup.nominations);

      // Restore handled fouls
      setHandledFouls(nightStateBackup.handledFouls);

      // Restore first day eliminations count
      setFirstDayEliminations(nightStateBackup.firstDayEliminations);

      // Clear eliminated players state
      setEliminatedPlayers([]);
      setEliminatedPlayerNumbers([]);
      setEliminationType("killed");

      // DON'T clear backup here - keep it for potential second undo
      // setNightStateBackup(null);

      // Force re-render of components
      setRenderKey((prev) => prev + 1);

      console.log(
        "Night state restoration completed, new playersLeaved should be:",
        nightStateBackup.gameState.playersLeaved
      );
    } else {
      console.log(
        "No night backup available for restoration - backup was cleared or never created"
      );
    }

    // Return to night phase to redo night actions
    setAppState(AppState.GAME_NIGHT);
  };

  const markFoulAsHandled = useCallback((playerIndex: number, foulCount: number) => {
    const foulKey = `${playerIndex}-${foulCount}`;
    setHandledFouls((prev) => new Set(prev).add(foulKey));
  }, []);

  const getGamePhaseNumber = (): number => {
    // Returns the game phase number for playersLeavedGP
    // Now gamePhase already contains the correct value:
    // -3 = player setup (not all players)
    // -2 = player setup (all players)
    // -1 = role assignment
    // 0 = first day
    // 1 = first night
    // 2 = second day
    // 3 = second night
    // etc.
    return gameState.gamePhase;
  };

  const getNextFirstTalkingPlayer = (
    currentFirstTalk: number,
    playersLeaved: number[]
  ) => {
    // Find the next alive player after the current first talking player
    for (let i = 1; i <= 10; i++) {
      const nextPlayerIndex = (currentFirstTalk + i) % 10;
      const nextPlayerNumber = nextPlayerIndex + 1;

      if (!playersLeaved.includes(nextPlayerNumber)) {
        return nextPlayerIndex;
      }
    }

    // Fallback: return current if no alive players found (shouldn't happen)
    return currentFirstTalk;
  };

  const getOrderedPlayersFromFirstTalk = (
    firstTalkIndex: number,
    playersLeaved: number[]
  ) => {
    // Create ordered list starting from first talking player
    const orderedPlayers: number[] = [];

    for (let i = 0; i < 10; i++) {
      const playerIndex = (firstTalkIndex + i) % 10;
      const playerNumber = playerIndex + 1;

      if (!playersLeaved.includes(playerNumber)) {
        orderedPlayers.push(playerNumber);
      }
    }

    return orderedPlayers;
  };

  const handleNightComplete = (nightActions: any) => {
    // Save state before processing night actions (for potential undo after this night)
    // This creates a fresh backup each time night completes
    saveNightState();

    // Process night actions
    const newState: any = {
      playersLeaved: [...gameState.playersLeaved], // Always initialize with current state
      playersLeavedGP: [...gameState.playersLeavedGP],
      aliveDon: gameState.aliveDon,
      aliveSheriff: gameState.aliveSheriff,
    };

    // Handle shooting
    if (nightActions.shooting && !nightActions.mafiaTarget) {
      // Add to playersLeavingNow instead of playersLeaved
      // They will be moved to playersLeaved when farewell completes
      // But only if they're not already in playersLeaved (e.g., disqualified during night)
      const isAlreadyLeaved = gameState.playersLeaved.includes(nightActions.shooting);

      if (!isAlreadyLeaved) {
        const newPlayersLeavingNow = [
          ...gameState.playersLeavingNow,
          nightActions.shooting,
        ];
        newState.playersLeavingNow = newPlayersLeavingNow;
      } else {
        // Player already left (disqualified), just preserve current state
        newState.playersLeavingNow = [...gameState.playersLeavingNow];
      }

      // Update role counts
      const eliminatedRole = gameRoles[nightActions.shooting - 1];
      if (eliminatedRole === "sheriff") {
        newState.aliveSheriff = 0;
      } else if (eliminatedRole === "don") {
        newState.aliveDon = 0;
      }
    }

    // Handle checks
    if (nightActions.donCheck && !nightActions.donMissed) {
      const newDonChecks = [...gameState.proverkiDon, nightActions.donCheck];
      newState.proverkiDon = newDonChecks;
    } else if (nightActions.donMissed) {
      const newDonChecks = [...gameState.proverkiDon, 0];
      newState.proverkiDon = newDonChecks;
    }

    if (nightActions.sheriffCheck && !nightActions.sheriffMissed) {
      const newSheriffChecks = [
        ...gameState.proverkiSheriff,
        nightActions.sheriffCheck,
      ];
      newState.proverkiSheriff = newSheriffChecks;
    } else if (nightActions.sheriffMissed) {
      const newSheriffChecks = [...gameState.proverkiSheriff, 0];
      newState.proverkiSheriff = newSheriffChecks;
    }

    // Handle shooting record
    const newOtstrel = [...gameState.otstrel];
    if (nightActions.shooting && !nightActions.mafiaTarget) {
      newOtstrel.push(nightActions.shooting);
    } else {
      newOtstrel.push(0);
      
      // Check for draw condition (3 consecutive mafia misses)
      if (gameState.nightCount > 0) {
        const currentAlivePlayers = newState.playersLeaved.length;
        const totalPlayers = 10;
        const aliveCount = totalPlayers - currentAlivePlayers;
        
        // Check if this is consecutive miss with same player count
        if ((drawCount === 0 && drawPlayers === 0) || drawPlayers === aliveCount) {
          // Same player count as last miss - increment counter
          setDrawCount(prev => prev + 1);
          setDrawPlayers(aliveCount);
          
          if (drawCount + 1 >= 3) {
            // Third consecutive miss - declare draw
            newState.pobeda = "draw";
            newState.endTime = getCurrentLocalDateTime();
            updateGameState(newState);
            setAppState(AppState.GAME_RESULTS);
            return; // Stop processing
          }
        } else {
          // Different player count - reset counter
          setDrawCount(1);
          setDrawPlayers(aliveCount);
        }
      }
    }
    newState.otstrel = newOtstrel;

    // Update penalty lists: tomorrow becomes today, and clear both tomorrow and old today
    // Players who were in penaltyListToday have already served their penalty
    newState.penaltyListToday = gameState.penaltyListTomorrow;
    newState.penaltyListTomorrow = [];

    // Update first talking player for the new day (before showing last words)
    // Combine playersLeaved and playersLeavingNow for firstTalk calculation
    const allLeftPlayers = [
      ...newState.playersLeaved,
      ...(newState.playersLeavingNow || []),
    ];
    const nextFirstTalk = getNextFirstTalkingPlayer(
      gameState.firstTalk,
      allLeftPlayers
    );
    newState.firstTalk = nextFirstTalk;
    // Day phase: 0, 2, 4, 6... (nightCount * 2)
    newState.gamePhase = gameState.nightCount * 2;

    updateGameState(newState);

    // Clear nominations for the new day
    setNominations({});

    // Check if someone was killed
    if (nightActions.shooting && !nightActions.mafiaTarget) {
      const killedPlayer = gamePlayers[nightActions.shooting - 1];
      const killedPlayerNumber = nightActions.shooting;

      // Check if killed player was already in playersLeaved (disqualified before/during night)
      // If so, they're not in playersLeavingNow and should skip farewell
      const isInPlayersLeavingNow = (newState.playersLeavingNow || []).includes(killedPlayerNumber);

      if (!isInPlayersLeavingNow) {
        // Player already disqualified, skip LastWords/MorningTransition
        checkGameEndConditions(newState, true);
      } else {
        // Player needs farewell - set up last words
        setEliminatedPlayers([killedPlayer]);
        setEliminatedPlayerNumbers([killedPlayerNumber]);
        setEliminationType("killed");

        // Check if this is first night (nightCount is 1 after being incremented when entering night)
        if (gameState.nightCount === 1) {
          // First night - show morning transition (keep gamePhase = 2 for second day)
          setAppState(AppState.MORNING_TRANSITION);
        } else {
          setAppState(AppState.LAST_WORDS);
        }
      }
    } else {
      // No kills, check game end conditions and transition to day phase
      checkGameEndConditions(newState, true);
    }
  };

  const handleBestMove = (guesses: number[]) => {
    // Calculate best move score
    let bestMoveScore = 0;
    guesses.forEach((playerNum) => {
      const role = gameRoles[playerNum - 1];
      if (role === "black" || role === "don") {
        bestMoveScore++;
      }
    });

    updateGameState({
      vanga: guesses,
      bestMove: bestMoveScore,
    });
  };

  const handleMorningContinue = () => {
    // Move players from playersLeavingNow to playersLeaved
    // This is when they actually leave the game (after best move/farewell)
    // Filter out players who are already in playersLeaved (e.g., disqualified during best move)
    const playersToMove = gameState.playersLeavingNow.filter(
      playerNum => !gameState.playersLeaved.includes(playerNum)
    );

    const newPlayersLeaved = [
      ...gameState.playersLeaved,
      ...playersToMove,
    ];

    // Continue to next day or check game end
    // firstTalk was already updated in handleNightComplete, so just check win conditions
    let aliveRed = 0;
    let aliveBlack = 0;

    gameRoles.forEach((role, index) => {
      const playerNumber = index + 1;
      if (!newPlayersLeaved.includes(playerNumber)) {
        if (role === "red" || role === "sheriff") {
          aliveRed++;
        } else if (role === "black" || role === "don") {
          aliveBlack++;
        }
      }
    });

    // Add to playersLeavedGP with correct phase (only for players not already there)
    // handleMorningContinue is always after first night kill, so use previous phase (night)
    const currentPhase = getGamePhaseNumber();
    const phaseNumber = currentPhase - 1; // Player was killed at night, current phase is day
    const newPlayersLeavedGP = [
      ...gameState.playersLeavedGP,
      ...playersToMove.map(() => phaseNumber),
    ];

    // Check win conditions FIRST, then update state with correct phase or 99
    if (aliveBlack === 0) {
      // Citizens win
      updateGameState({
        playersLeaved: newPlayersLeaved,
        playersLeavedGP: newPlayersLeavedGP,
        playersLeavingNow: [],
        pobeda: "red",
        gamePhase: 99,
        endTime: getCurrentLocalDateTime(),
      });
      setDayStateBackup(null);
      setAppState(AppState.GAME_RESULTS);
    } else if (aliveBlack >= aliveRed) {
      // Mafia wins
      updateGameState({
        playersLeaved: newPlayersLeaved,
        playersLeavedGP: newPlayersLeavedGP,
        playersLeavingNow: [],
        pobeda: "black",
        gamePhase: 99,
        endTime: getCurrentLocalDateTime(),
      });
      setDayStateBackup(null);
      setAppState(AppState.GAME_RESULTS);
    } else {
      // Game continues - move players and go to day
      updateGameState({
        playersLeaved: newPlayersLeaved,
        playersLeavedGP: newPlayersLeavedGP,
        playersLeavingNow: [],
      });
      // Clear nominations for new day
      setNominations({});
      setDayStateBackup(null);
      setNightStateBackup(null);
      // Force re-render to reset voting button state
      setRenderKey((prev) => prev + 1);
      setAppState(AppState.GAME_DAY);
    }
  };

  const handleLastWordsComplete = () => {
    // If game already ended (e.g., via PPK), don't change phase
    if (gameState.pobeda && gameState.pobeda !== "?") {
      return;
    }

    // Move players from playersLeavingNow to playersLeaved
    // This is when they actually leave the game (after farewell)
    // Filter out players who are already in playersLeaved (e.g., disqualified during farewell)
    const playersToMove = gameState.playersLeavingNow.filter(
      playerNum => !gameState.playersLeaved.includes(playerNum)
    );

    const newPlayersLeaved = [
      ...gameState.playersLeaved,
      ...playersToMove,
    ];

    // Clear eliminated players state
    setEliminatedPlayers([]);
    setEliminatedPlayerNumbers([]);

    if (eliminationType === "voted") {
      // After voting last words, check game end conditions before going to night
      let aliveRed = 0;
      let aliveBlack = 0;

      gameRoles.forEach((role, index) => {
        const playerNumber = index + 1;
        if (!newPlayersLeaved.includes(playerNumber)) {
          if (role === "red" || role === "sheriff") {
            aliveRed++;
          } else if (role === "black" || role === "don") {
            aliveBlack++;
          }
        }
      });

      // Add to playersLeavedGP with correct phase (only for players not already there)
      // Voted players leave during day phase, so use current phase
      const currentPhase = getGamePhaseNumber();
      const phaseNumber = currentPhase;
      const newPlayersLeavedGP = [
        ...gameState.playersLeavedGP,
        ...playersToMove.map(() => phaseNumber),
      ];

      // Check win conditions and update state in one call
      if (aliveBlack === 0) {
        // Citizens win
        updateGameState({
          playersLeaved: newPlayersLeaved,
          playersLeavedGP: newPlayersLeavedGP,
          playersLeavingNow: [],
          pobeda: "red",
          gamePhase: 99, // Game results phase
          endTime: getCurrentLocalDateTime(),
        });
        setDayStateBackup(null);
        setAppState(AppState.GAME_RESULTS);
        return;
      } else if (aliveBlack >= aliveRed) {
        // Mafia wins
        updateGameState({
          playersLeaved: newPlayersLeaved,
          playersLeavedGP: newPlayersLeavedGP,
          playersLeavingNow: [],
          pobeda: "black",
          gamePhase: 99, // Game results phase
          endTime: getCurrentLocalDateTime(),
        });
        setDayStateBackup(null);
        setAppState(AppState.GAME_RESULTS);
        return;
      }

      // Game continues - move players and go to night
      setVotingInProgress(false); // Reset voting state
      setNominations({}); // Clear nominations when going to night
      const nextNightCount = gameState.nightCount + 1;
      updateGameState({
        playersLeaved: newPlayersLeaved,
        playersLeavedGP: newPlayersLeavedGP,
        playersLeavingNow: [],
        nightCount: nextNightCount,
        gamePhase: nextNightCount * 2 - 1, // Night phase: 1, 3, 5, 7...
      });
      setAppState(AppState.GAME_NIGHT);
    } else {
      // After night kill last words, check game end and go to day phase
      // firstTalk was already updated in handleNightComplete, so just check win conditions
      let aliveRed = 0;
      let aliveBlack = 0;

      gameRoles.forEach((role, index) => {
        const playerNumber = index + 1;
        if (!newPlayersLeaved.includes(playerNumber)) {
          if (role === "red" || role === "sheriff") {
            aliveRed++;
          } else if (role === "black" || role === "don") {
            aliveBlack++;
          }
        }
      });

      // Add to playersLeavedGP with correct phase
      // Killed players leave after night, current phase is day, so use previous phase (night)
      const currentPhase = getGamePhaseNumber();
      const phaseNumber = currentPhase - 1;
      const newPlayersLeavedGP = [
        ...gameState.playersLeavedGP,
        ...playersToMove.map(() => phaseNumber),
      ];

      // Check win conditions and update state in one call
      if (aliveBlack === 0) {
        // Citizens win
        updateGameState({
          playersLeaved: newPlayersLeaved,
          playersLeavedGP: newPlayersLeavedGP,
          playersLeavingNow: [],
          pobeda: "red",
          gamePhase: 99,
          endTime: getCurrentLocalDateTime(),
        });
        setDayStateBackup(null);
        setAppState(AppState.GAME_RESULTS);
      } else if (aliveBlack >= aliveRed) {
        // Mafia wins
        updateGameState({
          playersLeaved: newPlayersLeaved,
          playersLeavedGP: newPlayersLeavedGP,
          playersLeavingNow: [],
          pobeda: "black",
          gamePhase: 99,
          endTime: getCurrentLocalDateTime(),
        });
        setDayStateBackup(null);
        setAppState(AppState.GAME_RESULTS);
      } else {
        // Game continues - move players and go to day
        updateGameState({
          playersLeaved: newPlayersLeaved,
          playersLeavedGP: newPlayersLeavedGP,
          playersLeavingNow: [],
        });
        // Clear nominations for new day
        setNominations({});
        setDayStateBackup(null);
        // Force re-render to reset voting button state
        setRenderKey((prev) => prev + 1);
        setAppState(AppState.GAME_DAY);
      }
    }
  };

  const checkGameEndConditions = (
    currentState: any,
    shouldTransitionToDay: boolean = false
  ) => {
    // Count alive players by team
    // Consider both playersLeaved and playersLeavingNow as eliminated for win condition checks
    const allLeftPlayers = [
      ...currentState.playersLeaved,
      ...(currentState.playersLeavingNow || []),
    ];

    let aliveRed = 0;
    let aliveBlack = 0;

    gameRoles.forEach((role, index) => {
      const playerNumber = index + 1;
      if (!allLeftPlayers.includes(playerNumber)) {
        if (role === "red" || role === "sheriff") {
          aliveRed++;
        } else if (role === "black" || role === "don") {
          aliveBlack++;
        }
      }
    });

    // Check win conditions
    if (aliveBlack === 0) {
      // Citizens win
      updateGameState({
        pobeda: "red",
        gamePhase: 99, // Game results phase
        endTime: getCurrentLocalDateTime(),
      });
      // Clear backup since game is ending
      setDayStateBackup(null);
      setAppState(AppState.GAME_RESULTS);
    } else if (aliveBlack >= aliveRed) {
      // Mafia wins
      updateGameState({
        pobeda: "black",
        gamePhase: 99, // Game results phase
        endTime: getCurrentLocalDateTime(),
      });
      // Clear backup since game is ending
      setDayStateBackup(null);
      setAppState(AppState.GAME_RESULTS);
    } else {
      // Game continues
      if (shouldTransitionToDay) {
        // Update first talking player for the new day
        const nextFirstTalk = getNextFirstTalkingPlayer(
          gameState.firstTalk,
          allLeftPlayers
        );
        updateGameState({
          firstTalk: nextFirstTalk,
          gamePhase: gameState.nightCount * 2, // Day phase: 0, 2, 4, 6...
          // Note: penaltyListToday and penaltyListTomorrow are already updated in handleNightComplete
        });

        // Clear nominations for new day
        setNominations({});

        // Clear backup since we're starting a new day
        setDayStateBackup(null);

        setAppState(AppState.GAME_DAY);
      }
      // Otherwise, stay in current phase
    }
  };

  const handlePlayerPunish = (playerIndex: number) => {
    // Handle player punishment (fouls)
    const currentFouls = gameState.playerFauls[playerIndex] || 0;
    const newFoulCount = currentFouls + 1;

    const newFauls = [...gameState.playerFauls];
    newFauls[playerIndex] = newFoulCount;

    updateGameState({
      playerFauls: newFauls,
    });

    // Note: Protection against double-clicks is handled by processingFoulRef
    // in DayPhase/NightPhase components (100ms debounce).
    // handledFouls is used only to prevent showing the punishment modal twice,
    // not to prevent adding fouls.

    // For 3rd and 4th fouls, the PlayerPunishment modal will handle the penalty
    // Don't automatically add to penalty lists here
  };

  const handlePlayerFoulAction = (
    playerIndex: number,
    action:
      | "add"
      | "reset"
      | "disqualify"
      | "penalty-today"
      | "penalty-tomorrow"
  ) => {
    const newFauls = [...gameState.playerFauls];
    const playerNumber = playerIndex + 1;

    switch (action) {
      case "add":
        const newFoulCount = (newFauls[playerIndex] || 0) + 1;
        newFauls[playerIndex] = newFoulCount;
        updateGameState({ playerFauls: newFauls });

        // Don't mark as handled here - let the component detect and show dialog first
        break;

      case "reset":
        newFauls[playerIndex] = 0;
        // Remove from penalty lists
        const newPenaltyToday = gameState.penaltyListToday.filter(
          (p) => p !== playerNumber
        );
        const newPenaltyTomorrow = gameState.penaltyListTomorrow.filter(
          (p) => p !== playerNumber
        );
        updateGameState({
          playerFauls: newFauls,
          penaltyListToday: newPenaltyToday,
          penaltyListTomorrow: newPenaltyTomorrow,
        });

        // Clear handled fouls for this player
        setHandledFouls((prev) => {
          const newSet = new Set(prev);
          Array.from(newSet).forEach((key) => {
            if (key.startsWith(`${playerIndex}-`)) {
              newSet.delete(key);
            }
          });
          return newSet;
        });
        break;

      case "disqualify":
        handleDisqualifyPlayer(playerIndex);
        break;

      case "penalty-today":
        const newPenaltyTodayList = [
          ...gameState.penaltyListToday,
          playerNumber,
        ];
        updateGameState({
          penaltyListToday: newPenaltyTodayList,
        });
        break;

      case "penalty-tomorrow":
        const newPenaltyTomorrowList = [
          ...gameState.penaltyListTomorrow,
          playerNumber,
        ];
        updateGameState({
          penaltyListTomorrow: newPenaltyTomorrowList,
        });
        break;
    }
  };

  const handleDisqualifyPlayer = (playerIndex: number) => {
    // Disqualify player (remove from game)
    const playerNumber = playerIndex + 1;

    // Check if player is already in playersLeaved or playersLeavingNow
    const isAlreadyLeaved = gameState.playersLeaved.includes(playerNumber);
    const isInLeavingNow = (gameState.playersLeavingNow || []).includes(playerNumber);

    // If player is in playersLeavingNow, move them to playersLeaved and remove from playersLeavingNow
    let newPlayersLeaved: number[];
    let newPlayersLeavingNow: number[];

    if (isInLeavingNow) {
      // Player is saying farewell - move them directly to playersLeaved
      newPlayersLeaved = [...gameState.playersLeaved, playerNumber];
      newPlayersLeavingNow = (gameState.playersLeavingNow || []).filter(p => p !== playerNumber);
    } else if (isAlreadyLeaved) {
      // Player is already in playersLeaved - no change
      newPlayersLeaved = [...gameState.playersLeaved];
      newPlayersLeavingNow = [...(gameState.playersLeavingNow || [])];
    } else {
      // Player is alive - add to playersLeaved
      newPlayersLeaved = [...gameState.playersLeaved, playerNumber];
      newPlayersLeavingNow = [...(gameState.playersLeavingNow || [])];
    }

    const newPlayersLeavedPenalty = [
      ...gameState.playersLeavedPenalty,
      playerNumber,
    ];

    // Only add to playersLeavedGP if player wasn't already in playersLeaved
    const phaseNumber = getGamePhaseNumber();
    const newPlayersLeavedGP = isAlreadyLeaved
      ? [...gameState.playersLeavedGP]
      : [...gameState.playersLeavedGP, phaseNumber];

    // Clear nominations involving disqualified player
    const newNominations = { ...nominations };
    
    // Remove nomination made BY the disqualified player
    if (newNominations[playerIndex.toString()]) {
      delete newNominations[playerIndex.toString()];
    }
    
    // Remove nominations made TO the disqualified player
    Object.keys(newNominations).forEach((key) => {
      if (newNominations[key] === playerNumber.toString()) {
        delete newNominations[key];
      }
    });
    
    setNominations(newNominations);

    // Update role counts
    const eliminatedRole = gameRoles[playerIndex];
    const newState: any = {
      playersLeaved: newPlayersLeaved,
      playersLeavingNow: newPlayersLeavingNow,
      playersLeavedPenalty: newPlayersLeavedPenalty,
      playersLeavedGP: newPlayersLeavedGP,
    };

    if (eliminatedRole === "sheriff") {
      newState.aliveSheriff = 0;
    } else if (eliminatedRole === "don") {
      newState.aliveDon = 0;
    }

    updateGameState(newState);

    // Check game end conditions after disqualification (don't change phase)
    setTimeout(() => {
      checkGameEndConditions(newState, false);
    }, 100);
  };

  const handlePPK = (playerIndex: number, winningTeam: "red" | "black") => {
    // PPK (Opposite team wins) - immediate game end
    const playerNumber = playerIndex + 1;

    updateGameState({
      pobeda: winningTeam,
      endTime: getCurrentLocalDateTime(),
      ppkPlayer: playerNumber,
    });

    setAppState(AppState.GAME_RESULTS);
  };

  const handleShowRoles = () => {
    // Show roles to judge/moderator
    const mafiaPlayers = gameRoles
      .map((role, index) => ({ role, index }))
      .filter(({ role }) => role === "black" || role === "don")
      .map(({ index }) => index + 1);

    const sheriffPlayer = gameRoles.findIndex((role) => role === "sheriff") + 1;

    alert(
      `Sheriff: ${sheriffPlayer}\nDon: ${
        gameRoles.findIndex((role) => role === "don") + 1
      }\nMafia: ${mafiaPlayers.join(", ")}`
    );
  };

  const handleTimeoutToggle = () => {
    // Handle timeout toggle functionality
    console.log("Timeout toggled");
  };

  const handleTimerStateChange = useCallback((accumulated: number, startedAt: number | undefined) => {
    updateGameState({
      timerAccumulated: accumulated,
      timerStartedAt: startedAt,
    });
  }, [updateGameState]);

  const handleTechnicalBreak = () => {
    // Set night phase for streamsync (OBS mute microphone) without changing gameState
    const nightPhaseForBreak = gameState.nightCount * 2 + 1;
    setTechnicalBreakGamePhase(nightPhaseForBreak);
    console.log("[TechnicalBreak] Override gamePhase for stream:", nightPhaseForBreak);
  };

  const handleTechnicalBreakEnd = () => {
    // Clear technical break override
    setTechnicalBreakGamePhase(null);
    console.log("[TechnicalBreak] Cleared gamePhase override");
  };

  const handleResultsSubmit = async () => {
    // Handle successful game submission
    resetAllGameState();

    // Return to appropriate screen based on game type and refresh data
    if (selectedTournament) {
      // Tournament game - return to tournament seats
      setAppState(AppState.TOURNAMENT_SEATS);
    } else if (selectedClub) {
      // Club game - refresh club data and return to club events
      try {
        // Reload club data to get updated event list and games
        const clubsResponse = await apiService.loadClubs();
        if (clubsResponse.success) {
          const updatedClub = clubsResponse.success.find(
            (c: Club) => c.id === selectedClub.id
          );
          if (updatedClub) {
            setSelectedClub(updatedClub);
          }
        }
      } catch (error) {
        console.error("Failed to reload club data:", error);
      }
      setAppState(AppState.CLUB_EVENTS);
    } else {
      // Fallback to main menu
      setAppState(AppState.MAIN_MENU);
    }
  };

  const handleBackToMenu = () => {
    resetAllGameState();

    // Additional resets specific to returning to menu
    setGameTableNumber(1);
    setGameNumber(1);
    setSelectedClub(null);
    setSelectedTournament(null);
    setCurrentEvent(null);
    setEventParticipants([]);
    setProtocolPlayerIds([]);

    setAppState(AppState.MAIN_MENU);
  };

  const handleCancelPlayerSetup = () => {
    // Reset game state
    resetAllGameState();

    // Return to appropriate screen based on game type
    if (selectedTournament) {
      // Tournament game - return to tournament seats
      setAppState(AppState.TOURNAMENT_SEATS);
    } else if (selectedClub) {
      // Club game - return to club events
      setAppState(AppState.CLUB_EVENTS);
    } else {
      // Fallback to main menu
      setAppState(AppState.MAIN_MENU);
    }
  };

  const renderCurrentView = () => {
    switch (appState) {
      case AppState.LOGIN:
        return <Login onLogin={handleLogin} />;

      case AppState.MAIN_MENU:
        return (
          <MainMenu
            language={language}
            onLanguageChange={handleLanguageChange}
            onTournamentSelect={handleTournamentSelect}
            onClubSelect={handleClubSelect}
            onLogout={handleLogout}
            userName={userData?.nickname || ""}
            userAvatar={userData?.avatar || ""}
          />
        );

      case AppState.TOURNAMENT_SEATS:
        return selectedTournament ? (
          <TournamentSeats
            language={language}
            tournament={selectedTournament}
            onBack={() => setAppState(AppState.MAIN_MENU)}
            onGameSelect={handleTournamentGameSelect}
          />
        ) : null;

      case AppState.PLAYER_SETUP:
        return (
          <PlayerSetup
            language={language}
            clubId={selectedClub?.id}
            streamLink={streamLink}
            frequentPlayers={selectedClub?.frequentPlayers}
            eventParticipants={eventParticipants}
            initialJudge={gameJudge} // Pass pre-set judge for tournament games
            onPlayersReady={handlePlayersReady}
            onPlayersChange={handlePlayersChange}
            onCancel={handleCancelPlayerSetup} // Use specific cancel handler
            gameNumber={gameNumber}
            onGameNumberChange={setGameNumber}
            onStreamSettingsChange={handleStreamSettingsChange}
            timerAccumulated={gameState.timerAccumulated}
            timerStartedAt={gameState.timerStartedAt}
            onTimerStateChange={handleTimerStateChange}
            onTimeoutToggle={handleTimeoutToggle}
            currentUser={
              userData
                ? {
                    id: userData.id,
                    nickname: userData.nickname,
                    avatar: userData.avatar,
                  }
                : undefined
            }
          />
        );

      case AppState.GAME_ROLES:
        return (
          <RoleAssignment
            language={language}
            players={gamePlayers}
            judge={gameJudge}
            streamError={streamError}
            onRolesReady={handleRolesReady}
            onCancel={handleBackToMenu}
            onTimeoutToggle={handleTimeoutToggle}
            timerAccumulated={gameState.timerAccumulated}
            timerStartedAt={gameState.timerStartedAt}
            onTimerStateChange={handleTimerStateChange}
          />
        );

      case AppState.GAME_DAY:
        return (
          <DayPhase
            key={`day-${gameState.nightCount}-${renderKey}`}
            language={language}
            players={gamePlayers}
            playerRoles={gameRoles}
            judge={gameJudge}
            playersLeaved={gameState.playersLeaved}
            playersLeavingNow={gameState.playersLeavingNow}
            playersLeavedGP={gameState.playersLeavedGP}
            playersLeavedPenalty={gameState.playersLeavedPenalty}
            playerFauls={gameState.playerFauls}
            penaltyListToday={gameState.penaltyListToday}
            firstTalk={gameState.firstTalk}
            nightCount={gameState.nightCount}
            handledFouls={handledFouls}
            nominations={nominations}
            votingInProgress={votingInProgress}
            streamError={streamError}
            streamLink={streamLink}
            getOrderedPlayersFromFirstTalk={getOrderedPlayersFromFirstTalk}
            onNominationChange={handleNominationChange}
            onSaveDayState={saveDayState}
            onVotingStart={handleVotingStart}
            onVotingComplete={handleInlineVotingComplete}
            onTechnicalBreak={handleTechnicalBreak}
            onTechnicalBreakEnd={handleTechnicalBreakEnd}
            onSetVotingInProgress={handleSetVotingInProgress}
            onGoToNight={handleGoToNight}
            onPlayerPunish={handlePlayerPunish}
            onPlayerFoulAction={handlePlayerFoulAction}
            onMarkFoulAsHandled={markFoulAsHandled}
            onDisqualifyPlayer={handleDisqualifyPlayer}
            onPPK={handlePPK}
            onLanguageChange={handleLanguageChange}
            onStreamSettingsChange={handleStreamSettingsChange}
            onTimeoutToggle={handleTimeoutToggle}
            timerAccumulated={gameState.timerAccumulated}
            timerStartedAt={gameState.timerStartedAt}
            onTimerStateChange={handleTimerStateChange}
            onCancel={handleBackToMenu}
          />
        );

      case AppState.GAME_VOTING:
        return (
          <Voting
            language={language}
            players={gamePlayers}
            playersLeaved={gameState.playersLeaved}
            nominations={nominations}
            nightCount={gameState.nightCount}
            firstTalk={gameState.firstTalk}
            getOrderedPlayersFromFirstTalk={getOrderedPlayersFromFirstTalk}
            onVotingComplete={handleVotingComplete}
            onCancel={() => setAppState(AppState.GAME_DAY)}
          />
        );

      case AppState.LAST_WORDS:
        // LastWords is now handled inline in DayPhase, so redirect to day phase
        return (
          <DayPhase
            key={`lastwords-${gameState.nightCount}-${renderKey}`}
            language={language}
            players={gamePlayers}
            playerRoles={gameRoles}
            judge={gameJudge}
            playersLeaved={gameState.playersLeaved}
            playersLeavingNow={gameState.playersLeavingNow}
            playersLeavedGP={gameState.playersLeavedGP}
            playersLeavedPenalty={gameState.playersLeavedPenalty}
            playerFauls={gameState.playerFauls}
            penaltyListToday={gameState.penaltyListToday}
            firstTalk={gameState.firstTalk}
            nightCount={gameState.nightCount}
            handledFouls={handledFouls}
            nominations={nominations}
            eliminatedPlayers={eliminatedPlayers}
            eliminatedPlayerNumbers={eliminatedPlayerNumbers}
            eliminationType={eliminationType}
            votingInProgress={votingInProgress}
            streamLink={streamLink}
            getOrderedPlayersFromFirstTalk={getOrderedPlayersFromFirstTalk}
            onNominationChange={handleNominationChange}
            onSaveDayState={saveDayState}
            onVotingStart={handleVotingStart}
            onVotingComplete={handleInlineVotingComplete}
            onSetVotingInProgress={handleSetVotingInProgress}
            onGoToNight={handleGoToNight}
            onPlayerPunish={handlePlayerPunish}
            onPlayerFoulAction={handlePlayerFoulAction}
            onMarkFoulAsHandled={markFoulAsHandled}
            onDisqualifyPlayer={handleDisqualifyPlayer}
            onPPK={handlePPK}
            onLastWordsComplete={handleLastWordsComplete}
            onLastWordsUndo={
              eliminationType === "voted" ? handleBackToDay : handleUndoNight
            }
            onLanguageChange={handleLanguageChange}
            onStreamSettingsChange={handleStreamSettingsChange}
            onTimeoutToggle={handleTimeoutToggle}
            timerAccumulated={gameState.timerAccumulated}
            timerStartedAt={gameState.timerStartedAt}
            onTimerStateChange={handleTimerStateChange}
            onTechnicalBreak={handleTechnicalBreak}
            onTechnicalBreakEnd={handleTechnicalBreakEnd}
            onCancel={handleBackToMenu}
          />
        );

      case AppState.GAME_NIGHT:
        return (
          <NightPhase
            language={language}
            players={gamePlayers}
            playerRoles={gameRoles}
            judge={gameJudge}
            playersLeaved={gameState.playersLeaved}
            playerFauls={gameState.playerFauls}
            nightCount={gameState.nightCount}
            aliveDon={gameState.aliveDon}
            aliveSheriff={gameState.aliveSheriff}
            handledFouls={handledFouls}
            streamError={streamError}
            onMorning={handleNightComplete}
            onPlayerPunish={handlePlayerPunish}
            onPlayerFoulAction={handlePlayerFoulAction}
            onMarkFoulAsHandled={markFoulAsHandled}
            onDisqualifyPlayer={handleDisqualifyPlayer}
            onPPK={handlePPK}
            onBackToDay={handleBackToDay}
            onTimeoutToggle={handleTimeoutToggle}
            timerAccumulated={gameState.timerAccumulated}
            timerStartedAt={gameState.timerStartedAt}
            onTimerStateChange={handleTimerStateChange}
            onShowRoles={handleShowRoles}
            onCancel={handleBackToMenu}
          />
        );

      case AppState.MORNING_TRANSITION:
        const eliminatedPlayer =
          gameState.otstrel[gameState.otstrel.length - 1];
        return (
          <DayPhase
            key={`morning-${gameState.nightCount}-${renderKey}`}
            language={language}
            players={gamePlayers}
            playerRoles={gameRoles}
            judge={gameJudge}
            playersLeaved={gameState.playersLeaved}
            playersLeavingNow={gameState.playersLeavingNow}
            playersLeavedGP={gameState.playersLeavedGP}
            playersLeavedPenalty={gameState.playersLeavedPenalty}
            playerFauls={gameState.playerFauls}
            penaltyListToday={gameState.penaltyListToday}
            firstTalk={gameState.firstTalk}
            nightCount={gameState.nightCount}
            handledFouls={handledFouls}
            nominations={nominations}
            votingInProgress={votingInProgress}
            streamLink={streamLink}
            morningTransition={{
              eliminatedPlayer: eliminatedPlayer || undefined,
              isFirstNight: gameState.nightCount === 1,
              firstDayEliminations: firstDayEliminations,
              onBestMove: handleBestMove,
              onLastWords: handleLastWordsComplete,
              onContinue: handleMorningContinue,
              onUndo: handleUndoNight,
            }}
            getOrderedPlayersFromFirstTalk={getOrderedPlayersFromFirstTalk}
            onNominationChange={handleNominationChange}
            onSaveDayState={saveDayState}
            onVotingStart={handleVotingStart}
            onVotingComplete={handleInlineVotingComplete}
            onSetVotingInProgress={handleSetVotingInProgress}
            onGoToNight={handleGoToNight}
            onPlayerPunish={handlePlayerPunish}
            onPlayerFoulAction={handlePlayerFoulAction}
            onMarkFoulAsHandled={markFoulAsHandled}
            onDisqualifyPlayer={handleDisqualifyPlayer}
            onPPK={handlePPK}
            onLanguageChange={handleLanguageChange}
            onStreamSettingsChange={handleStreamSettingsChange}
            onTimeoutToggle={handleTimeoutToggle}
            timerAccumulated={gameState.timerAccumulated}
            timerStartedAt={gameState.timerStartedAt}
            onTimerStateChange={handleTimerStateChange}
            onTechnicalBreak={handleTechnicalBreak}
            onTechnicalBreakEnd={handleTechnicalBreakEnd}
            onCancel={handleBackToMenu}
          />
        );

      case AppState.GAME_RESULTS:
        return (
          <GameResults
            language={language}
            players={gamePlayers}
            playerRoles={gameRoles}
            judge={gameJudge}
            gameState={gameState}
            clubId={selectedClub?.id}
            eventId={currentEvent?.id}
            tournamentId={selectedTournament?.tournament_id}
            partNumber={gameState.currPart}
            protocolPlayerIds={protocolPlayerIds}
            tableNumber={gameTableNumber}
            gameNumber={gameNumber}
            onSubmit={handleResultsSubmit}
            onCancel={handleBackToMenu}
          />
        );

      case AppState.CLUB_EVENTS:
        return selectedClub ? (
          <ClubEvents
            language={language}
            club={selectedClub}
            initialEventId={currentEvent?.id}
            onStartGame={handleStartGame}
            onBack={handleBackToMenu}
          />
        ) : (
          <div className="container">
            <div className="main" style={{ marginTop: "80px" }}>
              <p>No club selected</p>
              <button className="btn btn-secondary" onClick={handleBackToMenu}>
                {dictionaries[language].ph.back}
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="container">
            <div className="main" style={{ marginTop: "80px" }}>
              <p>Game state: {appState}</p>
              <button className="btn btn-secondary" onClick={handleBackToMenu}>
                {dictionaries[language].ph.back}
              </button>
            </div>
          </div>
        );
    }
  };

  return <div className="App app-shell">{renderCurrentView()}</div>;
}

export default App;
